import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Smartphone, RectangleHorizontal, Car, Plus, Gift, Sparkles, Loader2, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { MyAdCard } from './MyAdCard';
import { monthLabel } from './AdPlanPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATS = [
  { id: 'phones', icon: Smartphone, ar: 'أرقام الهواتف', en: 'Phone Numbers', route: '/dashboard/ads/new-phone', color: '#10B981' },
  { id: 'plates', icon: RectangleHorizontal, ar: 'لوحات السيارات', en: 'Car Plates', route: '/dashboard/ads/new-plate', color: '#F59E0B' },
  { id: 'vehicles', icon: Car, ar: 'المركبات', en: 'Vehicles', route: '/dashboard/ads/new-vehicle', color: '#1E9BD9' },
];

export const buildAdPayUrl = (ad, isAr) => {
  const p = ad.payment || {};
  const label = isAr ? `رسوم إعلان (${ad.title})` : `Ad fee (${ad.title})`;
  const q = new URLSearchParams({
    amount: String(p.amount || 0),
    currency: p.currency || 'AED',
    description: label,
    reference: p.reference || '',
    return_to: '/dashboard?tab=myads',
    ad: ad.id,
  });
  return `/pay?${q.toString()}`;
};

export const MyAdsTab = ({ isAr, isDarkMode, myAds, onDelete, onRefresh }) => {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [filter, setFilter] = useState('all');
  const [checking, setChecking] = useState(null);

  const loadQuota = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/user/ads/pricing`, { headers: getAuthHeaders() });
      setQuota(r.data);
    } catch { setQuota(null); }
  }, []);

  useEffect(() => { loadQuota(); }, [loadQuota, myAds]);

  const handleEdit = (category, ad) => navigate(`/dashboard/ads/edit/${category}/${ad.id}`);
  const handlePay = (category, ad) => navigate(buildAdPayUrl(ad, isAr));
  const handleCheckPayment = async (category, ad) => {
    setChecking(ad.id);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/user/ads/${ad.id}/confirm-payment`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || (isAr ? 'تم تفعيل الإعلان' : 'Ad activated'));
      onRefresh?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.info((typeof d === 'string' ? d : d?.message) || (isAr ? 'لم يتم تأكيد الدفع بعد' : 'Payment not confirmed yet'));
    } finally { setChecking(null); }
  };

  const card = isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light';
  const text = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const currency = quota?.currency || 'AED';
  const visibleCats = filter === 'all' ? CATS.map((c) => c.id) : [filter];
  const totalAds = CATS.reduce((n, c) => n + (myAds[c.id]?.length || 0), 0);

  return (
    <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${card}`} data-testid="myads-section">
      {/* Title */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className={`text-xl font-bold flex items-center ${text}`}>
            <Store className="h-6 w-6 me-2 text-[#1E9BD9]" />
            {isAr ? 'إعلاناتي' : 'My Ads'}
            <span className={`ms-2 text-xs font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-600'}`} data-testid="myads-total">{totalAds}</span>
          </h2>
          <p className={`text-sm mt-1 flex items-center gap-1.5 ${muted}`}>
            <Gift className="h-4 w-4 text-emerald-500" />
            {isAr
              ? `أول إعلانين في كل فئة مجاناً لمدة ${quota?.categories?.phones?.free_duration_days || 30} يوماً، وما بعدهما بباقات (مدة ← سعر) تُحدَّد من إدارة المنصة.`
              : `Your first 2 ads per category are free for ${quota?.categories?.phones?.free_duration_days || 30} days; additional ads use duration plans set by the platform admin.`}
          </p>
        </div>
      </div>

      {/* Category cards (all framed) */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {CATS.map((c) => {
          const q = quota?.categories?.[c.id];
          const count = myAds[c.id]?.length || 0;
          const Icon = c.icon;
          const nextFree = q ? q.next_is_free : true;
          const minTier = q?.tiers?.length ? q.tiers.reduce((m, t) => (t.price_aed < m.price_aed ? t : m), q.tiers[0]) : null;
          return (
            <div
              key={c.id}
              className={`group relative overflow-hidden rounded-2xl border p-4 flex flex-col gap-3 transition-all duration-300 hover:-translate-y-0.5 cgm-shimmer ${isDarkMode ? 'bg-[#0A0B10]/60 border-[#1E9BD9]/20 hover:border-[#1E9BD9]/50' : 'bg-white border-[#1E9BD9]/25 hover:border-[#1E9BD9]/60 shadow-sm'}`}
              data-testid={`ads-category-card-${c.id}`}
            >
              <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${c.color}, #F5A962)` }} />
              <div className="absolute -top-10 -end-10 w-28 h-28 rounded-full blur-2xl opacity-25 pointer-events-none" style={{ background: c.color }} />
              <div className="flex items-center justify-between relative">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${c.color}1f` }}>
                    <Icon className="h-5 w-5" style={{ color: c.color }} />
                  </span>
                  <div>
                    <h3 className={`font-bold text-sm ${text}`}>{isAr ? c.ar : c.en}</h3>
                    <p className={`text-[11px] ${muted}`} data-testid={`ads-count-${c.id}`}>{count} {isAr ? 'إعلان' : 'ads'}</p>
                  </div>
                </div>
              </div>
              <div className={`relative text-[11px] font-semibold rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5 ${nextFree ? 'bg-emerald-500/10 text-emerald-500' : q && !q.paid_available ? 'bg-slate-500/10 text-slate-400' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`} data-testid={`ads-quota-${c.id}`}>
                {nextFree ? <Gift className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
                {q
                  ? nextFree
                    ? (isAr ? `متبقي ${q.free_remaining} من ${q.free_ads} مجاناً` : `${q.free_remaining} of ${q.free_ads} free left`)
                    : minTier
                      ? (isAr ? `الإعلان التالي من ${minTier.price_aed} ${currency} / ${monthLabel(minTier.months, true)}` : `Next ad from ${minTier.price_aed} ${currency} / ${monthLabel(minTier.months, false)}`)
                      : (isAr ? 'النشر المدفوع غير متاح حالياً' : 'Paid publishing unavailable')
                  : <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
              <button
                type="button"
                onClick={() => navigate(c.route)}
                className="relative w-full h-10 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:shadow-[0_6px_18px_rgba(30,155,217,0.4)] transition-shadow inline-flex items-center justify-center gap-1.5"
                data-testid={`add-${c.id === 'phones' ? 'phone' : c.id === 'plates' ? 'plate' : 'vehicle'}-ad-btn`}
              >
                <Plus className="h-4 w-4" />
                {isAr ? 'إضافة إعلان' : 'Add Ad'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="ads-filter-chips">
        {[{ id: 'all', ar: 'الكل', en: 'All' }, ...CATS].map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setFilter(c.id)}
            className={`px-3.5 h-8 rounded-full text-xs font-semibold border transition-colors ${filter === c.id ? 'bg-[#1E9BD9] border-[#1E9BD9] text-white' : isDarkMode ? 'border-white/10 text-slate-300 hover:border-[#1E9BD9]/50' : 'border-slate-200 text-slate-600 hover:border-[#1E9BD9]/50'}`}
            data-testid={`ads-filter-${c.id}`}
          >
            {isAr ? c.ar : c.en}
          </button>
        ))}
      </div>

      {/* Grid */}
      {totalAds === 0 ? (
        <div className={`rounded-2xl border border-dashed p-10 text-center ${isDarkMode ? 'border-white/15' : 'border-slate-300'}`} data-testid="ads-empty-state">
          <Store className="h-12 w-12 mx-auto mb-3 text-[#1E9BD9]/60" />
          <p className={`font-bold ${text}`}>{isAr ? 'لا توجد إعلانات بعد' : 'No ads yet'}</p>
          <p className={`text-sm mt-1 ${muted}`}>{isAr ? 'ابدأ بإضافة أول إعلان مجاني من البطاقات أعلاه.' : 'Add your first free ad from the cards above.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="myads-grid">
          {visibleCats.map((category) =>
            (myAds[category] || []).map((ad) => (
              <MyAdCard
                key={ad.id}
                ad={ad}
                category={category}
                isAr={isAr}
                isDarkMode={isDarkMode}
                onDelete={onDelete}
                onEdit={handleEdit}
                onPay={handlePay}
                onCheckPayment={handleCheckPayment}
                checkingPayment={checking === ad.id}
              />
            ))
          )}
        </div>
      )}

      {/* Business upsell */}
      <div className="mt-8 relative overflow-hidden rounded-2xl p-6 text-white bg-[#0B1220] border border-[#1E9BD9]/30">
        <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-gradient-to-br from-[#1E9BD9]/30 to-[#F5A962]/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <Info className="h-5 w-5 text-[#38BDF8] mt-0.5 shrink-0" />
          <div>
            <h3 className="text-lg font-bold mb-1">{isAr ? 'تبيع بكميات أكبر؟' : 'Selling at scale?'}</h3>
            <p className="text-white/75 text-sm">
              {isAr
                ? 'افتح متجرك على CloudGM Business: متجر باسمك، لوحة مبيعات متقدمة، أولوية في نتائج البحث ودعم مخصص.'
                : 'Open your store on CloudGM Business: a branded store, advanced sales dashboard, search priority and dedicated support.'}
            </p>
            <a href="https://cloudgm.business" target="_blank" rel="noreferrer" className="inline-flex mt-3 h-9 px-4 items-center rounded-xl text-sm font-bold text-black bg-[#F5A962] hover:bg-[#e09952] transition-colors" data-testid="open-store-cta">
              {isAr ? 'افتح متجرك الآن' : 'Open Your Store Now'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
